import { Injectable, inject } from '@angular/core';
import { forkJoin, from, map, Observable, of, switchMap } from 'rxjs';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';
import { GoodsSkuControllerService } from '@app/api/goods-service/api/goodsSkuController.service';
import { GoodsSkuPageVO } from '@app/api/goods-service/model/goodsSkuPageVO';
import { PageInfoGoodsSkuPageVO } from '@app/api/goods-service/model/pageInfoGoodsSkuPageVO';
import { GoodsStockControllerService } from '@app/api/stock-service/api/goodsStockController.service';
import { GoodsStockResVO } from '@app/api/stock-service/model/goodsStockResVO';
import { PackageOperationControllerService } from '@app/api/stock-service/api/packageOperationController.service';
import { PackageOperationHistoryQueryDTO } from '@app/api/stock-service/model/packageOperationHistoryQueryDTO';
import { PackageOperationRequestDTO } from '@app/api/stock-service/model/packageOperationRequestDTO';
import { PackageOperationVO } from '@app/api/stock-service/model/packageOperationVO';
import { PackageStockCheckResVO } from '@app/api/stock-service/model/packageStockCheckResVO';
import { PackageUnpackRequestDTO } from '@app/api/stock-service/model/packageUnpackRequestDTO';
import { PagePackageOperationVO } from '@app/api/stock-service/model/pagePackageOperationVO';
import {
  PackageInventoryItem,
  PackageWarehouseStock,
} from './package-operation.config';

export interface PackageInventoryFilters {
  skuCode?: string;
  skuName?: string;
}

export interface PackageInventoryPage {
  items: PackageInventoryItem[];
  totalRecords: number;
}

@Injectable()
export class PackageOperationService {
  private readonly goodsSkuApi = inject(GoodsSkuControllerService);
  private readonly goodsStockApi = inject(GoodsStockControllerService);
  private readonly operationApi = inject(PackageOperationControllerService);

  inventory(
    filters: PackageInventoryFilters,
    page: number,
    size: number,
  ): Observable<PackageInventoryPage> {
    return this.goodsSkuApi
      .getList3(
        undefined, // keyword
        undefined, // spuName
        undefined, // spuCode
        undefined, // spuType
        1, // setFlag
        undefined, // categoryIdList
        undefined, // status
        filters.skuName || undefined, // skuName
        undefined, // searchMode
        filters.skuCode || undefined, // skuCode
        undefined, // startDate
        undefined, // endDate
        undefined, // skuIds
        undefined, // lastUpdatedAfter
        undefined, // channelShopId
        undefined, // warehouseCode
        undefined, // showFlag
        page,
        size,
        'lastUpdateDate',
        false,
      )
      .pipe(
        switchMap((response) => this.toJson<PageInfoGoodsSkuPageVO>(response)),
        switchMap((response) => {
          const packageSkus = response.pageContent ?? [];
          const totalRecords = response.totalElements ?? packageSkus.length;
          const skuCodes = packageSkus
            .map((sku) => sku.skuCode)
            .filter((code): code is string => !!code);

          if (skuCodes.length === 0) {
            return of<{
              packageSkus: GoodsSkuPageVO[];
              stocksBySku: Record<string, GoodsStockResVO[]>;
              totalRecords: number;
            }>({ packageSkus, stocksBySku: {}, totalRecords });
          }

          return this.goodsStockApi
            .findStocksBySkuCodes({ skuCodes })
            .pipe(
              switchMap((stocksBySku) =>
                this.toJson<{ [key: string]: GoodsStockResVO[] }>(stocksBySku),
              ),
              map((stocksBySku) => ({
                packageSkus,
                stocksBySku,
                totalRecords,
              })),
            );
        }),
        map(({ packageSkus, stocksBySku, totalRecords }) => ({
          items: packageSkus.map((sku) =>
            this.toInventoryItem(sku, stocksBySku[sku.skuCode ?? ''] ?? []),
          ),
          totalRecords,
        })),
      );
  }

  history(
    warehouseId: number,
    packageSkuCode: string,
    page: number,
    size: number,
  ): Observable<PagePackageOperationVO> {
    const query: PackageOperationHistoryQueryDTO = {
      page,
      size,
      sortBy: 'creationDate',
      ascending: false,
      warehouseId,
      packageSkuCode,
    };
    return this.operationApi
      .history(query, {})
      .pipe(switchMap((response) => this.toJson<PagePackageOperationVO>(response)));
  }

  historyForPackage(
    packageSkuCode: string,
    warehouseIds: number[],
  ): Observable<PackageOperationVO[]> {
    const uniqueWarehouseIds = [...new Set(warehouseIds)];
    if (uniqueWarehouseIds.length === 0) {
      return of([]);
    }

    return forkJoin(
      uniqueWarehouseIds.map((warehouseId) =>
        this.history(warehouseId, packageSkuCode, 0, 100),
      ),
    ).pipe(
      map((responses) =>
        responses
          .flatMap((response) => response.content ?? [])
          .sort(
            (left, right) =>
              this.timestamp(right.creationDate) - this.timestamp(left.creationDate),
          ),
      ),
    );
  }

  checkStock(
    warehouseId: number,
    packageSkuCode: string,
    packageQty: number,
  ): Observable<PackageStockCheckResVO> {
    return this.goodsStockApi
      .checkPackageStock({
        warehouseId,
        packageSkuQtyList: [{ skuCode: packageSkuCode, stockNum: packageQty }],
      })
      .pipe(
        switchMap((response) => this.toJson<PackageStockCheckResVO>(response)),
      );
  }

  eligibleSourcePacks(
    warehouseId: number,
    packageSkuCode: string,
  ): Observable<PackageOperationVO[]> {
    return this.operationApi
      .eligible(warehouseId, packageSkuCode)
      .pipe(switchMap((response) => this.toJson<PackageOperationVO[]>(response)));
  }

  pack(request: PackageOperationRequestDTO): Observable<PackageOperationVO> {
    return this.operationApi
      .pack(request)
      .pipe(switchMap((response) => this.toJson<PackageOperationVO>(response)));
  }

  unpack(request: PackageUnpackRequestDTO): Observable<PackageOperationVO> {
    return this.operationApi
      .unpack(request)
      .pipe(switchMap((response) => this.toJson<PackageOperationVO>(response)));
  }

  private toInventoryItem(
    sku: GoodsSkuPageVO,
    stockRows: GoodsStockResVO[],
  ): PackageInventoryItem {
    const warehouses = stockRows
      .filter((stock): stock is GoodsStockResVO & { warehouseId: number } =>
        stock.warehouseId !== undefined,
      )
      .map<PackageWarehouseStock>((stock) => ({
        id: stock.warehouseId,
        code: stock.warehouseCode ?? '',
        name: stock.warehouseName ?? '',
      }));

    return {
      id: sku.id?.toString() ?? sku.skuCode ?? '',
      skuCode: sku.skuCode ?? '',
      skuName: sku.skuName ?? '',
      spuCode: sku.spuCode ?? '',
      spuName: sku.spuName ?? '',
      fulfillmentMode: sku.fulfillmentMode,
      availableQty: stockRows.reduce(
        (total, stock) =>
          total +
          Math.max(
            0,
            (stock.stockNum ?? 0) -
              (stock.reservedNum ?? 0) -
              (stock.preemptionNum ?? 0) -
              (stock.lockNum ?? 0) -
              (stock.damagedNum ?? 0),
          ),
        0,
      ),
      lastUpdateDate: sku.lastUpdateDate,
      warehouses,
    };
  }

  private toJson<T>(response: T): Observable<T> {
    if (BlobConverterUtil.isBlob(response)) {
      return from(BlobConverterUtil.convertToJson(response) as Promise<T>);
    }
    return of(response);
  }

  private timestamp(value?: string): number {
    const timestamp = value ? Date.parse(value) : 0;
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }
}
